<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import axios from 'axios';

const props = defineProps({
    profil: Object
});

const activeTab = ref('general');
const saving = ref(false);
const modalType = ref('error');
const errorModalContent = ref([]);

// Forms
const generalForm = ref({
    company_description: ''
});
let descEditor = null;
let visionEditor = null;
let missionEditor = null;

const visionMissionForm = ref({
    vision_text: '',
    mission_text: ''
});

const valuesForm = ref([]);
const videoForm = ref(null);

// Original data for change detection
let originalDescription = '';
let originalVision = '';
let originalMission = '';
let originalValues = [];

// Show modal helper
const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    try {
        const modalEl = document.getElementById('notificationModal');
        if (modalEl && typeof bootstrap !== 'undefined') {
            const modal = new bootstrap.Modal(modalEl);
            modal.show();
        }
    } catch (e) {
        alert(errorModalContent.value.join('\n'));
    }
};

// Normalize HTML for comparison
const normalizeHtml = (html) => {
    if (!html) return '';
    return html.replace(/\s+/g, ' ').replace(/>\s+</g, '><').trim();
};

// Initialize Data
const initData = () => {
    if (props.profil) {
        generalForm.value = {
            company_description: props.profil.company_description || ''
        };
        visionMissionForm.value = {
            vision_text: props.profil.vision_text || '',
            mission_text: props.profil.mission_text || ''
        };
        // Parse values_data if it's a string or array
        try {
            if (typeof props.profil.values_data === 'string') {
                valuesForm.value = JSON.parse(props.profil.values_data) || [];
            } else if (Array.isArray(props.profil.values_data)) {
                valuesForm.value = props.profil.values_data;
            } else {
                valuesForm.value = [];
            }
        } catch (e) {
            valuesForm.value = [];
        }
    }
    // Ensure at least one empty value
    if (valuesForm.value.length === 0) {
        addValue();
    }
};

// Store original data after init
const storeOriginalData = () => {
    originalDescription = normalizeHtml(generalForm.value.company_description);
    originalVision = normalizeHtml(visionMissionForm.value.vision_text);
    originalMission = normalizeHtml(visionMissionForm.value.mission_text);
    originalValues = JSON.stringify(valuesForm.value);
};

// General & Description Update
const updateGeneral = async () => {
    saving.value = true;
    
    // Delay for spinner visibility
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Sync CKEditor
    if (descEditor) {
        generalForm.value.company_description = descEditor.getData();
    }
    
    // Validation
    if (!generalForm.value.company_description?.trim()) {
        saving.value = false;
        showModal('error', 'Deskripsi Perusahaan wajib diisi');
        return;
    }
    
    // Check for changes
    if (normalizeHtml(generalForm.value.company_description) === originalDescription) {
        saving.value = false;
        showModal('warning', 'Tidak ada perubahan data yang disimpan.');
        return;
    }
    
    try {
        const response = await axios.put('/admin/profil/update', {
            company_description: generalForm.value.company_description
        });
        if (response.data.status === 'success') {
            originalDescription = normalizeHtml(generalForm.value.company_description);
        }
    } catch (e) {
        showModal('error', 'Gagal menyimpan data umum');
    } finally {
        saving.value = false;
    }
};

// Vision Mission Update
const updateVisionMission = async () => {
    saving.value = true;
    
    // Delay for spinner visibility
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Sync CKEditor
    if (visionEditor) {
        visionMissionForm.value.vision_text = visionEditor.getData();
    }
    if (missionEditor) {
        visionMissionForm.value.mission_text = missionEditor.getData();
    }
    
    // Validation
    const errors = [];
    if (!visionMissionForm.value.vision_text?.trim()) errors.push('Visi wajib diisi');
    if (!visionMissionForm.value.mission_text?.trim()) errors.push('Misi wajib diisi');
    
    if (errors.length > 0) {
        saving.value = false;
        showModal('error', errors);
        return;
    }
    
    // Check for changes
    const currentVision = normalizeHtml(visionMissionForm.value.vision_text);
    const currentMission = normalizeHtml(visionMissionForm.value.mission_text);
    if (currentVision === originalVision && currentMission === originalMission) {
        saving.value = false;
        showModal('warning', 'Tidak ada perubahan data yang disimpan.');
        return;
    }
    
    try {
        const response = await axios.put('/admin/profil/update', {
            vision_text: visionMissionForm.value.vision_text,
            mission_text: visionMissionForm.value.mission_text
        });
        if (response.data.status === 'success') {
            originalVision = currentVision;
            originalMission = currentMission;
        }
    } catch (e) {
        showModal('error', 'Gagal menyimpan visi misi');
    } finally {
        saving.value = false;
    }
};

// Values Update
const addValue = () => {
    valuesForm.value.push({ title: '', description: '' });
};

const removeValue = (index) => {
    valuesForm.value.splice(index, 1);
};

const updateValues = async () => {
    saving.value = true;
    
    // Delay for spinner visibility
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Validation
    const hasEmpty = valuesForm.value.some(v => !v.title?.trim() || !v.description?.trim());
    if (hasEmpty) {
        saving.value = false;
        showModal('error', 'Semua field judul dan deskripsi nilai wajib diisi');
        return;
    }
    
    // Check for changes
    if (JSON.stringify(valuesForm.value) === originalValues) {
        saving.value = false;
        showModal('warning', 'Tidak ada perubahan data yang disimpan.');
        return;
    }
    
    try {
        const response = await axios.put('/admin/profil/update-values', {
            values: valuesForm.value
        });
        if (response.data.status === 'success') {
            originalValues = JSON.stringify(valuesForm.value);
        }
    } catch (e) {
        showModal('error', 'Gagal menyimpan nilai perusahaan');
    } finally {
        saving.value = false;
    }
};

// Video Upload
const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 200 * 1024 * 1024) {
        showModal('error', 'Ukuran video maksimal 200MB');
        return;
    }

    const formData = new FormData();
    formData.append('video_tour', file);

    saving.value = true;
    try {
        await axios.post('/admin/profil/video', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        window.location.reload();
    } catch (e) {
        showModal('error', 'Gagal upload video');
    } finally {
        saving.value = false;
    }
};

// Init CKEditor for all rich text fields
const initCKEditor = () => {
    if (typeof CKEDITOR === 'undefined') {
        console.error('CKEditor not loaded');
        return;
    }
    
    const editorConfig = {
        height: 200,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'links', items: ['Link', 'Unlink'] }
        ],
        removePlugins: 'elementspath',
        resize_enabled: true
    };
    
    // Company Description
    if (CKEDITOR.instances['company_description']) {
        CKEDITOR.instances['company_description'].destroy();
    }
    descEditor = CKEDITOR.replace('company_description', { ...editorConfig, height: 300 });
    descEditor.setData(generalForm.value.company_description || '');
    
    // Vision
    if (CKEDITOR.instances['vision_text']) {
        CKEDITOR.instances['vision_text'].destroy();
    }
    visionEditor = CKEDITOR.replace('vision_text', editorConfig);
    visionEditor.setData(visionMissionForm.value.vision_text || '');
    
    // Mission
    if (CKEDITOR.instances['mission_text']) {
        CKEDITOR.instances['mission_text'].destroy();
    }
    missionEditor = CKEDITOR.replace('mission_text', editorConfig);
    missionEditor.setData(visionMissionForm.value.mission_text || '');
};

onMounted(() => {
    initData();
    storeOriginalData();
    nextTick(() => {
        initCKEditor();
    });
});

onBeforeUnmount(() => {
    const editors = [descEditor, visionEditor, missionEditor];
    editors.forEach(editor => {
        if (editor) {
            try {
                editor.destroy();
            } catch (e) {
                console.warn('CKEditor destroy error:', e);
            }
        }
    });
    descEditor = null;
    visionEditor = null;
    missionEditor = null;
});
</script>

<template>
    <Head title="Profil Perusahaan" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Profil Perusahaan</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item">
                                    <Link :href="route('dashboard.index')"><i class="mdi mdi-home-outline"></i></Link>
                                </li>
                                <li class="breadcrumb-item active" aria-current="page">Profil Perusahaan</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-header p-0 border-bottom-0">
                            <ul class="nav nav-tabs" role="tablist">
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'general' }" @click.prevent="activeTab = 'general'" href="#">Info Umum</a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'vision' }" @click.prevent="activeTab = 'vision'" href="#">Visi & Misi</a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'values' }" @click.prevent="activeTab = 'values'" href="#">Nilai Perusahaan</a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'video' }" @click.prevent="activeTab = 'video'" href="#">Video Tour</a>
                                </li>
                            </ul>
                        </div>
                        <div class="card-body">
                            <div class="tab-content">
                                <!-- General Tab -->
                                <div class="tab-pane fade" :class="{ 'show active': activeTab === 'general' }">
                                    <form @submit.prevent="updateGeneral">
                                        <div class="form-group mb-3">
                                            <label for="company_description" class="form-label">Deskripsi Perusahaan</label>
                                            <textarea id="company_description" name="company_description" class="form-control" rows="10"></textarea>
                                        </div>
                                        <div class="text-end">
                                            <button type="submit" class="btn btn-primary" :disabled="saving">
                                                <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                                                <i v-else class="ti-save-alt me-1"></i> Simpan
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <!-- Vision Mission Tab -->
                                <div class="tab-pane fade" :class="{ 'show active': activeTab === 'vision' }">
                                    <form @submit.prevent="updateVisionMission">
                                        <div class="form-group mb-3">
                                            <label for="vision_text" class="form-label">Visi</label>
                                            <textarea id="vision_text" name="vision_text" class="form-control" rows="5"></textarea>
                                        </div>
                                        <div class="form-group mb-3">
                                            <label for="mission_text" class="form-label">Misi</label>
                                            <textarea id="mission_text" name="mission_text" class="form-control" rows="8"></textarea>
                                        </div>
                                        <div class="text-end">
                                            <button type="submit" class="btn btn-primary" :disabled="saving">
                                                <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                                                <i v-else class="ti-save-alt me-1"></i> Simpan
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <!-- Values Tab -->
                                <div class="tab-pane fade" :class="{ 'show active': activeTab === 'values' }">
                                    <form @submit.prevent="updateValues">
                                        <div v-for="(val, index) in valuesForm" :key="index" class="card mb-3 border">
                                            <div class="card-body bg-light">
                                                <div class="d-flex justify-content-between mb-2">
                                                    <h6>Nilai #{{ index + 1 }}</h6>
                                                    <button type="button" class="btn btn-sm btn-danger time-circle" @click="removeValue(index)">
                                                        <i class="fa fa-trash"></i>
                                                    </button>
                                                </div>
                                                <div class="form-group mb-2">
                                                    <label :for="'value_title_' + index" class="form-label small">Judul Nilai</label>
                                                    <input :id="'value_title_' + index" :name="'value_title_' + index" type="text" v-model="val.title" class="form-control" placeholder="Judul Nilai">
                                                </div>
                                                <div class="form-group">
                                                    <label :for="'value_description_' + index" class="form-label small">Deskripsi</label>
                                                    <textarea :id="'value_description_' + index" :name="'value_description_' + index" v-model="val.description" class="form-control" rows="2" placeholder="Deskripsi"></textarea>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="mb-3">
                                            <button type="button" class="btn btn-sm btn-info" @click="addValue">
                                                <i class="fa fa-plus"></i> Tambah Nilai
                                            </button>
                                        </div>
                                        <div class="text-end">
                                            <button type="submit" class="btn btn-primary" :disabled="saving">
                                                <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                                                <i v-else class="ti-save-alt me-1"></i> Simpan
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <!-- Video Tab -->
                                <div class="tab-pane fade" :class="{ 'show active': activeTab === 'video' }">
                                    <div class="form-group mb-3">
                                        <label for="video_tour" class="form-label">Upload Video Tour (Max 200MB, MP4)</label>
                                        <input id="video_tour" name="video_tour" type="file" @change="handleVideoUpload" class="form-control" accept="video/mp4">
                                    </div>
                                    <div v-if="props.profil?.video_tour_box" class="mt-3">
                                        <h5>Video Saat Ini:</h5>
                                        <div class="ratio ratio-16x9" style="max-width: 600px;">
                                            <video controls>
                                                <source :src="'/storage/videos/' + props.profil.video_tour_box" type="video/mp4">
                                                Browser anda tidak mendukung tag video.
                                            </video>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Notification Modal -->
        <div class="modal fade" id="notificationModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header text-white" 
                        :class="{
                            'bg-danger': modalType === 'error',
                            'bg-warning text-dark': modalType === 'warning',
                            'bg-success': modalType === 'success',
                            'bg-info': modalType === 'info'
                        }">
                        <h5 class="modal-title">
                            <i class="me-2" :class="{
                                'ti-alert': modalType === 'error',
                                'fa fa-exclamation-triangle': modalType === 'warning',
                                'fa fa-check-circle': modalType === 'success',
                                'fa fa-info-circle': modalType === 'info'
                            }"></i>
                            {{ modalType === 'error' ? 'Peringatan Validasi' : modalType === 'warning' ? 'Peringatan' : modalType === 'success' ? 'Berhasil' : 'Informasi' }}
                        </h5>
                        <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut:</p>
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item" 
                                :class="{
                                    'list-group-item-danger': modalType === 'error',
                                    'list-group-item-warning': modalType === 'warning',
                                    'list-group-item-success': modalType === 'success',
                                    'list-group-item-info': modalType === 'info'
                                }" 
                                v-for="(msg, index) in errorModalContent" :key="index">
                                <i class="fa me-2" :class="{
                                    'fa-exclamation-circle': modalType === 'error',
                                    'fa-exclamation-triangle': modalType === 'warning',
                                    'fa-check-circle': modalType === 'success',
                                    'fa-info-circle': modalType === 'info'
                                }"></i>{{ msg }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn" 
                            :class="{
                                'btn-danger': modalType === 'error',
                                'btn-warning': modalType === 'warning',
                                'btn-success': modalType === 'success',
                                'btn-info': modalType === 'info'
                            }" 
                            data-bs-dismiss="modal">
                            <i class="fa fa-check-square-o me-2"></i>Mengerti
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
